import React from 'react';
import { TimeChart, formatMs, PERCENTILE_KEYS, PERCENTILE_COLORS } from '../charts';

// ── Percentile charts (latency, tokens per request, outbound timing) ─────────
// One series per percentile, the same seven on every card, so they are read against each
// other without re-learning the legend. Shared by the Dashboard's sections.
const PERCENTILE_LINES = PERCENTILE_KEYS.map(k => ({ key: k, label: k }));

// A bucket with no samples comes back from generate_series with its percentiles as 0 —
// the axis is kept deliberately, so a quiet period does not compress the time scale. But
// a chart where EVERY bucket is 0 draws seven flat lines along the baseline, which reads
// as "every response was instant" rather than "nothing was measured". That is worse than
// an empty state, so it becomes one.
export function latencyHasSamples(data) {
  return Array.isArray(data) && data.some(r => (r.samples || 0) > 0);
}

// The chart plus its legend. The legend is not optional here — seven lines cannot be told
// apart from the colour ramp alone, and the tooltip only helps once you already know which
// line you are chasing. `format` is the unit: milliseconds for the latency cards, a token
// count for tokens per request.
// `gapOnNull` is for a caller that blanks its empty buckets (the load tests' report, whose
// later phases leave most of the run without a request): the Dashboard keeps its zeros.
// `phases` (charts/phases.jsx) draws a load test's phases over the chart.
export default function PercentileChart({ data, height = 240, format = formatMs, formatX, phases,
                                          gapOnNull = false }) {
  return (
    <TimeChart
      data={data} labelKey="date"
      lines={PERCENTILE_LINES} colors={PERCENTILE_COLORS}
      height={height}
      formatY={format}
      formatValue={format}
      formatX={formatX}
      phases={phases}
      gapOnNull={gapOnNull}
      legend
      tooltipExtra={(row) => (
        /* The sample count is what makes a percentile honest. A bucket holding four
           calls has a p99 that is simply its slowest call, and nothing else on the
           chart distinguishes that from a p99 over ten thousand. */
        <div className="chart-tooltip-foot">
          <span className="chart-tooltip-label">Samples: </span>
          <span className="chart-tooltip-value">{(row.samples || 0).toLocaleString()}</span>
        </div>
      )} />
  );
}
